import React, { useState } from 'react';
import CustomButton from '../reusable/CustomButton';
import CustomTooltip from '../reusable/CustomTooltip';
import CustomConfirmationModal from '../reusable/CustomConfirmationModal';
import { executeWorkflowTransition } from '../../services/workflowService';
import GlobalStateContext from '../../context/GlobalStateContext';
import { useContext } from 'react';
import { IconButton } from '@mui/material';
import InfoIcon from '../assets/info-icon.svg';
import './UnlockDataUploadButton.css';

interface UnlockDataUploadButtonProps {
  onUnlockSuccess: (transitionResponse: any) => void;
}

const UnlockDataUploadButton: React.FC<UnlockDataUploadButtonProps> = ({ onUnlockSuccess }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { userDetails } = useContext(GlobalStateContext);
  const eoaId = userDetails.data?.eoaId;
  const accessToken = userDetails.data?.access_token;

  const handleUnlockData = async () => {
    if (!eoaId || !accessToken) return;

    try {
      setIsLoading(true);
      const transitionResponse = await executeWorkflowTransition(eoaId, accessToken, 'unlock_data_upload');
      onUnlockSuccess(transitionResponse);
    } catch (error) {
      console.error('Error unlocking data upload:', error);
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
    }
  };

  const handleConfirm = () => {
    handleUnlockData();
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  return (
    <>
      <div className="unlock-data-upload-container">
        <CustomButton
          variant="secondary"
          buttonText="Unlock data upload"
          buttonAction={() => setShowConfirmation(true)}
          loading={isLoading}
        />
        <CustomTooltip
          title="An XQ Admin can use the 'Unlock Data' button to reopen the data upload process if updates or corrections are needed. This allows district data leads to reupload files after data has been locked."
          placement="top"
          arrow={true}
        >
          <IconButton className="info-icon" sx={{ '&:hover': { backgroundColor: 'var(--background-white)' } }}>
            <img className="icon-size" src={InfoIcon} alt="Info" />
          </IconButton>
        </CustomTooltip>
      </div>

      <CustomConfirmationModal
        isOpen={showConfirmation}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title="Are you sure?"
        message="By confirming, you are unlocking the data upload page for the district."
        confirmText="Confirm"
        cancelText="Cancel"
        isLoading={isLoading}
      />
    </>
  );
};

export default UnlockDataUploadButton;
